import { debounce } from 'lodash-es';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import usePrevious from '@react-hook/previous';

import { handleClickSearch, handleKeyDown, query, setSearchQuery, toggleShowFullForm } from '../Actions/QuickSearch.action';
import { translate } from '../Services/translation';
import QuickSearchResult from './QuickSearchResult';

// debouncing function to 200ms so we don't send query request on every key stroke
const debouncedQuery = debounce((dispatch, text) => dispatch(query(text)), 200);
const MOBILE_BREAKPOINT = 767;
const PLACEHOLDER_KEYS = [
    'js.quicksearch.placeholder.example1',
    'js.quicksearch.placeholder.example2',
    'js.quicksearch.placeholder.example3',
];
const PLACEHOLDER_FALLBACK = {
    sv: [
        'Svarta gravidleggings',
        'Amnings-bh i svart',
        'Mjuka plagg for graviditeten',
    ],
    de: [
        'Suche nach schwarzen Leggings',
        'Probiere Still-BH in Schwarz',
        'Finde weiche Styles fur die Schwangerschaft',
    ],
    en: [
        'Search for black leggings',
        'Try nursing bra in black',
        'Find soft styles for pregnancy',
    ],
};

const getPlaceholderFallback = (lang) => {
    if (lang.startsWith('sv')) {
        return PLACEHOLDER_FALLBACK.sv;
    }

    if (lang.startsWith('de')) {
        return PLACEHOLDER_FALLBACK.de;
    }

    return PLACEHOLDER_FALLBACK.en;
};

const QuickSearch = () => {
    const { query, result, showResult, showFullForm, selectedItem } = useSelector((state) => state.quickSearch);
    const [lastClickedNode, setLastClickedNode] = useState(null);
    const [animatedPlaceholder, setAnimatedPlaceholder] = useState(translate('general.search'));
    const searchUrl = window.__litium.quickSearchUrl + (query.length > 0 ? `?q=${query}` : '');
    const searchContainer = useRef(null);
    const searchInput = useRef(null);
    const dispatch = useDispatch();
    const previousSelectedItem = usePrevious(selectedItem);

    const updateMobileSearchPosition = useCallback(() => {
        if (typeof window === 'undefined' || window.innerWidth > MOBILE_BREAKPOINT) {
            return;
        }

        const searchForm = searchContainer.current?.querySelector('.quick-search__form');
        if (!searchForm) {
            return;
        }

        const header = document.getElementById('header');
        if (!header) {
            searchForm.style.removeProperty('--quick-search-mobile-top');
            return;
        }

        const headerRect = header.getBoundingClientRect();
        const top = Math.max(Math.round(headerRect.bottom) + 8, 0);
        searchForm.style.setProperty('--quick-search-mobile-top', `${top}px`);
    }, []);

    const clickHandler = useCallback((event) => {
        setLastClickedNode(event.target);
    }, []);

    const placeholderPhrases = useMemo(() => {
        const lang = (document.documentElement?.lang || 'en').toLowerCase();
        const fallbackPhrases = getPlaceholderFallback(lang);

        return PLACEHOLDER_KEYS.map((key, index) => {
            const translatedPhrase = translate(key);
            return translatedPhrase !== key ? translatedPhrase : fallbackPhrases[index];
        }).filter(Boolean);
    }, []);

    useEffect(() => {
        // listen for click event to hide the search when clicking outside
        window.addEventListener('mousedown', clickHandler);
        return () => window.removeEventListener('mousedown', clickHandler);
    }, [clickHandler]);

    useEffect(() => {
        // set query value if it is avaialble in the Url
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has('q')) {
            const query = urlParams.get('q');
            dispatch(setSearchQuery(query));
        }
    }, [dispatch]);

    useEffect(() => {
        if (selectedItem !== previousSelectedItem) {
            const el = document.querySelector('.quick-search-result__item--selected');
            el &&
                el.scrollIntoView({
                    behavior: 'smooth',
                    block: 'end',
                    inline: 'nearest',
                });
        }
    }, [selectedItem, previousSelectedItem]);

    useEffect(() => {
        if (!showFullForm) {
            return undefined;
        }

        const scheduleUpdate = () => window.requestAnimationFrame(updateMobileSearchPosition);
        scheduleUpdate();

        window.addEventListener('resize', scheduleUpdate);
        window.addEventListener('scroll', scheduleUpdate, { passive: true });
        window.visualViewport?.addEventListener('resize', scheduleUpdate);
        window.visualViewport?.addEventListener('scroll', scheduleUpdate);

        return () => {
            window.removeEventListener('resize', scheduleUpdate);
            window.removeEventListener('scroll', scheduleUpdate);
            window.visualViewport?.removeEventListener('resize', scheduleUpdate);
            window.visualViewport?.removeEventListener('scroll', scheduleUpdate);
        };
    }, [showFullForm, updateMobileSearchPosition]);

    useEffect(() => {
        if (!placeholderPhrases.length) {
            setAnimatedPlaceholder(translate('general.search'));
            return undefined;
        }

        if (query.length > 0) {
            setAnimatedPlaceholder(translate('general.search'));
            return undefined;
        }

        let timeoutId;
        let phraseIndex = 0;
        let charIndex = 0;
        let deleting = false;

        const tick = () => {
            const phrase = placeholderPhrases[phraseIndex];
            const nextCharIndex = deleting ? Math.max(charIndex - 1, 0) : Math.min(charIndex + 1, phrase.length);
            const nextPlaceholder = phrase.slice(0, nextCharIndex);

            setAnimatedPlaceholder(nextPlaceholder);
            charIndex = nextCharIndex;

            if (!deleting && charIndex === phrase.length) {
                deleting = true;
                timeoutId = window.setTimeout(tick, 1600);
                return;
            }

            if (deleting && charIndex === 0) {
                deleting = false;
                phraseIndex = (phraseIndex + 1) % placeholderPhrases.length;
                timeoutId = window.setTimeout(tick, 320);
                return;
            }

            timeoutId = window.setTimeout(tick, deleting ? 32 : 62);
        };

        timeoutId = window.setTimeout(tick, 280);

        return () => window.clearTimeout(timeoutId);
    }, [placeholderPhrases, query.length]);

    return (
        <div className="quick-search" role="search" ref={searchContainer}>
            <button
                className="quick-search__link--block"
                onClick={(e) => {
                    e.preventDefault();
                    dispatch(toggleShowFullForm());
                    setTimeout(() => {
                        searchInput.current && searchInput.current.focus();
                    }, 0);
                }}
            >
                <i className="quick-search__icon"></i>
                <span className="quick-search__link-text">{translate('general.search')}</span>
            </button>
            <div className={`quick-search__form ${showFullForm ? 'quick-search__form--force-show' : ''}`} role="search">
                <i className="quick-search__icon" onClick={(e) => dispatch(toggleShowFullForm())}></i>
                <input
                    className="quick-search__input"
                    type="search"
                    placeholder={animatedPlaceholder}
                    autoComplete="off"
                    value={decodeURIComponent(query)}
                    onChange={(event) => {
                        const text = encodeURIComponent(event.target.value);
                        dispatch(setSearchQuery(text));
                        debouncedQuery(dispatch, text);
                    }}
                    onKeyDown={(event) => dispatch(handleKeyDown(event, { searchUrl }))}
                    ref={searchInput}
                    onBlur={() => {
                        if (searchContainer.current && !searchContainer.current.contains(lastClickedNode)) {
                            showFullForm && dispatch(toggleShowFullForm());
                        }
                    }}
                />
                <button
                    className="quick-search__submit-button"
                    type="button"
                    onClick={(e) => {
                        e.preventDefault();
                        dispatch(handleClickSearch({ searchUrl }));
                    }}
                >
                    <i className="quick-search__submit-icon"></i>
                    <span className="quick-search__submit-title">{translate('general.search')}</span>
                </button>
                {showResult && <QuickSearchResult result={result} selectedItem={selectedItem} searchUrl={searchUrl} />}
            </div>
        </div>
    );
};

export default QuickSearch;
